// KPI aziendali — pagina admin separata dalla BI: 16 gruppi di indicatori in 6 schede, filtro agente, export Excel, ottimizzata per il telefono
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gauge, Database, FileSpreadsheet, FileText, Loader2, UserCog } from "lucide-react";
import { openAuthenticatedPdf } from "../utils/pdf";
import { Card } from "../components/ui/card";
import BiFilters from "./bi/BiFilters";
import { Seg, fmtInt } from "./bi/biShared";
import KpiSales from "./kpi/KpiSales";
import KpiCustomers from "./kpi/KpiCustomers";
import KpiProducts from "./kpi/KpiProducts";
import KpiMix from "./kpi/KpiMix";
import KpiFinance from "./kpi/KpiFinance";
import KpiGrowth from "./kpi/KpiGrowth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { value: "sales", label: "Vendite", groups: "1 · 12 · 15" },
  { value: "customers", label: "Clienti", groups: "2 · 7 · 8 · 9" },
  { value: "products", label: "Prodotti", groups: "3 · 5" },
  { value: "mix", label: "Mix", groups: "9 · 10 · 11 · 16" },
  { value: "finance", label: "Incassi", groups: "4" },
  { value: "growth", label: "Crescita", groups: "6 · 13 · 14" },
];
const VIEWS = { sales: KpiSales, customers: KpiCustomers, products: KpiProducts, mix: KpiMix, finance: KpiFinance, growth: KpiGrowth };

const AdminKpiDashboard = () => {
  const [filters, setFilters] = useState({ preset: "month", date_from: "", date_to: "" });
  const [agent, setAgent] = useState("");
  const [tab, setTab] = useState("sales");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const params = useMemo(() => {
    const p = { preset: filters.preset };
    if (filters.preset === "custom" && filters.date_from && filters.date_to) Object.assign(p, { date_from: filters.date_from, date_to: filters.date_to });
    if (agent) p.agent = agent;
    return p;
  }, [filters.preset, filters.date_from, filters.date_to, agent]);

  useEffect(() => {
    if (filters.preset === "custom" && !(filters.date_from && filters.date_to)) return undefined;
    let alive = true;
    setLoading(true);
    setError(false);
    axios.get(`${API}/admin/kpi`, { params }).then((r) => { if (alive) setData(r.data); }).catch(() => { if (alive) { setError(true); toast.error("Errore caricamento KPI"); } }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [params, filters.preset, filters.date_from, filters.date_to, reloadKey]);

  const onReload = useCallback(() => setReloadKey((k) => k + 1), []);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/admin/kpi/export`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `KPI_${data?.period?.from || ""}_${data?.period?.to || ""}${agent ? "_agente" : ""}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel KPI scaricato");
    } catch {
      toast.error("Errore nell'export Excel");
    } finally {
      setExporting(false);
    }
  };

  const pdfQuery = () => new URLSearchParams(params).toString();
  const exportPdf = async () => {
    setExportingPdf(true);
    const label = agent ? `agente_${(data?.agent?.name || "").replace(/[^\w]+/g, "_")}` : "azienda";
    await openAuthenticatedPdf(`/admin/kpi/pdf?${pdfQuery()}`, { download: true, filename: `KPI_${label}_${data?.period?.from || ""}_${data?.period?.to || ""}.pdf` });
    setExportingPdf(false);
  };

  const View = VIEWS[tab];
  const current = TABS.find((t) => t.value === tab);
  const agents = data?.agent?.available || [];
  return (
    <div className="space-y-5" data-testid="kpi-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-stone-900 flex items-center gap-2"><Gauge className="w-6 h-6 text-[#8A1C2A]" /> KPI aziendali</h2>
          <p className="text-stone-500 text-sm">16 gruppi di indicatori calcolati dai DDT, dallo scadenziario e dal catalogo. Tocca una card per leggere la formula.</p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <button type="button" onClick={exportPdf} disabled={!data || exportingPdf} title={agent ? "PDF da inviare all'agente: solo i suoi clienti, senza margini, costi, sconti né dati di altri agenti, con le spiegazioni delle voci" : "PDF interno completo di tutta l'azienda, con margini, prezzi e mix per agente: non inviarlo agli agenti"}
            className={`h-11 px-4 rounded-xl border text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 ${agent ? "border-[#8A1C2A] bg-[#8A1C2A] text-white hover:bg-[#6B1420]" : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50"}`} data-testid="kpi-export-pdf-btn">
            {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className={`w-4 h-4 ${agent ? "text-white" : "text-[#8A1C2A]"}`} />} {agent ? "PDF per l'agente" : "PDF azienda (interno)"}
          </button>
          <button type="button" onClick={exportExcel} disabled={!data || exporting} className="h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm font-semibold text-stone-700 hover:bg-stone-50 inline-flex items-center gap-2 disabled:opacity-50" data-testid="kpi-export-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 text-emerald-700" />} Scarica Excel
          </button>
        </div>
      </div>

      <BiFilters filters={filters} onChange={setFilters} period={data?.period} showGranularity={false} />

      <label className="flex items-center gap-2 max-w-md" data-testid="kpi-agent-filter">
        <UserCog className="w-4 h-4 text-stone-500 shrink-0" />
        <select value={agent} onChange={(e) => setAgent(e.target.value)} className={`h-11 px-3 rounded-xl border bg-white text-sm flex-1 ${agent ? "border-[#8A1C2A] text-[#8A1C2A] font-semibold" : "border-stone-300 text-stone-700"}`} data-testid="kpi-agent-select">
          <option value="">Tutti gli agenti</option>
          {agents.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
        </select>
      </label>

      <div className="sticky top-[53px] z-10 -mx-2 px-2 py-1.5 bg-stone-50/95 backdrop-blur" data-testid="kpi-tabs">
        <Seg options={TABS} value={tab} onChange={setTab} testIdPrefix="kpi-tab" wrap />
        <p className="text-[11px] text-stone-400 mt-1 pl-1" data-testid="kpi-tab-groups">Gruppi del documento: {current.groups}{data?.agent?.name ? ` · agente: ${data.agent.name}` : ""}</p>
      </div>

      {loading && !data && <p className="text-sm text-stone-500" data-testid="kpi-loading">Caricamento KPI…</p>}
      {error && !data && <p className="text-sm text-red-700" data-testid="kpi-error">Impossibile caricare i KPI. Riprova.</p>}

      {data && (
        <div className={`space-y-5 transition-opacity ${loading ? "opacity-60" : ""}`}>
          {data.agent?.name && (
            <p className="text-xs text-[#8A1C2A] bg-[#8A1C2A]/5 border border-[#8A1C2A]/20 rounded-xl px-3 py-2" data-testid="kpi-agent-banner">
              Filtro attivo: solo DDT e documenti fiscali dei clienti di <b>{data.agent.name}</b>. <button type="button" className="underline font-semibold" onClick={() => setAgent("")} data-testid="kpi-agent-reset">Tutti gli agenti</button>
            </p>
          )}
          {data.sources.excluded_names?.length > 0 && (
            <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="kpi-exclusions-banner">
              Esclusi dalle statistiche: <b>{data.sources.excluded_names.join(", ")}</b> — {fmtInt(data.sources.excluded_docs)} DDT nello storico{data.finance?.excluded_docs ? ` e ${fmtInt(data.finance.excluded_docs)} documenti fiscali` : ""}.
            </p>
          )}
          <View data={data} onReload={onReload} />
          <Card className="bg-stone-50 border-stone-200 p-4 text-xs text-stone-600 space-y-1" data-testid="kpi-sources-footer">
            <p className="font-semibold text-stone-800 flex items-center gap-1.5"><Database className="w-3.5 h-3.5" /> Da dove derivano i numeri</p>
            <p><b>DDT</b> — {data.notes.ddt} Nel periodo: {fmtInt(data.sources.ddt_used)} DDT, {fmtInt(data.sources.lines)} righe; periodo precedente {fmtInt(data.sources.prev_docs)} DDT; storico {fmtInt(data.sources.history_docs)} DDT.</p>
            <p><b>Agente</b> — {data.notes.agent}</p>
            <p><b>Incassi</b> — {data.notes.finance}</p>
            <p><b>Catalogo</b> — {data.notes.catalog}</p>
            <p><b>Geografia</b> — {data.notes.geo}</p>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminKpiDashboard;
